// 거래명세서 삭제 실패 사유를 사람이 읽을 수 있는 문장으로 변환한다.
// firestore.rules(transactionStatements allow delete)는 다음만 허용한다:
//   - 발행자(회사) 계정이고
//   - status === 'draft' 이거나, createdAt + 7일 > 현재
// 즉 발행본(confirmed)은 작성 7일 이내만 삭제 가능하고, 작성일이 없는 옛 문서는 규칙 평가에서 거부된다.
// 조용한 실패(아무 안내 없이 안 지워짐)를 막기 위해 삭제 호출부는 반드시 이 함수를 써서 사유를 화면에 띄운다.

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

// Firestore Timestamp / Date / 숫자 / 문자열을 Date로 변환 (실패 시 null)
function toDateSafe(value) {
    if (!value) return null;
    try {
        if (typeof value.toDate === 'function') return value.toDate();
        if (value instanceof Date) return value;
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    } catch (e) {
        return null;
    }
}

// 로그인 계정이 이 명세서의 발행자(또는 같은 회사)인지 — rules의 hasCompanyAccess 근사치
function isIssuerAccount(statement, user) {
    const from = statement?.fromUserId;
    if (!from) return true;   // 판단 불가 시 발행자로 가정 (엉뚱한 안내 방지)
    const ids = [user?.uid, user?.companyId].filter(Boolean);
    return ids.includes(from);
}

/**
 * @param {Error} err       deleteDoc에서 던진 오류
 * @param {Object} statement 삭제하려던 명세서 문서 데이터(status, createdAt, fromUserId)
 * @param {Object} user      로그인 사용자 (uid, companyId)
 * @returns {string} 화면에 그대로 띄울 안내 문구
 */
export function getStatementDeleteErrorMessage(err, statement, user) {
    const code = err?.code || '';
    const status = statement?.status;

    if (code === 'permission-denied') {
        // 1) 발행자가 아닌 계정(수신 치과 등)
        if (!isIssuerAccount(statement, user)) {
            return '받은 명세서는 삭제할 수 없습니다.';
        }

        // 2) 발행본(confirmed) — 작성 7일 초과
        if (status === 'confirmed') {
            const created = toDateSafe(statement?.createdAt);
            if (!created) {
                return '작성일 정보가 없는 예전 거래명세서라 삭제할 수 없습니다.\n금액 정정이 필요하면 수정 후 재발행하거나 담당에게 문의해 주세요.';
            }
            if (Date.now() - created.getTime() > SEVEN_DAYS_MS) {
                return '발행 후 7일이 지난 거래명세서는 삭제할 수 없습니다.\n금액 정정이 필요하면 수정 후 재발행하거나 담당에게 문의해 주세요.';
            }
        }

        // 3) 그 밖의 권한 거부
        return `삭제 권한이 없어 삭제하지 못했습니다. (오류: ${code})\n담당에게 문의해 주세요.`;
    }

    // 4) 네트워크 등 그 외 실패
    const detail = code || err?.message || '알 수 없는 오류';
    return `삭제하지 못했습니다. (오류: ${detail})\n잠시 후 다시 시도하고, 계속 안 되면 담당에게 문의해 주세요.`;
}

export default getStatementDeleteErrorMessage;
